import { useCallback } from 'react';

import CurrencySign from 'components/price/CurrencySign.component';
import { useTranslation } from 'providers/GILT.provider';
import asObject from 'utils/objects/asObject.util';

const CashupTableRight = ({ reconcileList, byRangeModal, cashupData, updateData }) => {
  const { t } = useTranslation();

  const onInputChange = useCallback(
    (eventTargetValue, propName) => {
      let value = eventTargetValue;

      if (value === '') {
        value = 0;
      }

      if (Number.isNaN(value && value !== '.')) {
        return;
      }

      const updatedCashupData = {
        ...cashupData,
        [propName]: value,
      };

      updateData(updatedCashupData);
    },
    [cashupData, updateData],
  );

  const keyPress = (e) => {
    // backspace delete
    if (e.keyCode === 8) {
      const a = e.target.value.split('');
      a.pop();
      e.target.value = a.join('');
    }
  };

  const {
    pettyCashOut: pettyCashOutFromProps,
    cashInTillStart: cashInTillStartFromProps,
    differenceActualVsExpected,
    cashTakenToBank,
    expectedCashInTill,
    actualCashInTill,
    deposits_amount: depositsAmount,
    totalCash,
  } = asObject(cashupData);

  const cashTakings = depositsAmount ? totalCash + depositsAmount.cash : totalCash;

  const normalizeDayValue = (value) => (value >= 0 ? value : '');

  let pettyCashOut;
  let cashTakenToBankTotal;
  let cashInTillStart;
  let cashInTillEnd;

  if (byRangeModal === 'by-day') {
    pettyCashOut = normalizeDayValue(pettyCashOutFromProps);
    cashTakenToBankTotal = normalizeDayValue(cashTakenToBank);
    cashInTillStart = normalizeDayValue(cashInTillStartFromProps);
    cashInTillEnd = normalizeDayValue(actualCashInTill);
  } else if (reconcileList) {
    const { total_startCashInTill: start, total_endCashInTill: end, total_cashBanked: banked, total_pettyCash: petty } = reconcileList;

    if (typeof petty === 'number') {
      pettyCashOut = petty;
    }
    if (typeof banked === 'number') {
      cashTakenToBankTotal = banked;
    }
    if (typeof start === 'number') {
      cashInTillStart = start;
    }
    if (typeof end === 'number') {
      cashInTillEnd = end;
    }
  }

  return (
    <div data-bem="CashupTableRight" className="csh-table-wrap clearfix tables">
      <div className="tbl-shadow-container cfix">
        <table className="csh-table">
          <tbody>
            <tr>
              <td>
                <span>{t('cashUp.cashupTableRight.tableRow.tillCash')}</span>
                <div className="info">
                  <span className="tooltip">{t('cashUp.cashupTableRight.tooltip.tillCash')}</span>
                </div>
              </td>
              <td>
                {byRangeModal === 'by-day' && (
                  <input
                    type="text"
                    placeholder={t('common.placeholder.enterAmount')}
                    value={cashInTillStart}
                    onKeyDown={keyPress}
                    onChange={(event) => onInputChange(event.target.value, 'cashInTillStart')}
                  />
                )}
                {byRangeModal === 'by-range' && (
                  <span>
                    {typeof cashInTillStart === 'number' ? (
                      <>
                        <CurrencySign /> {cashInTillStart}
                      </>
                    ) : (
                      '-'
                    )}
                  </span>
                )}
              </td>
            </tr>
            <tr>
              <td>
                <span>{`+ ${t('cashUp.cashupTableRight.tableRow.cashPayments')}`}</span>
                <div className="info">
                  <span className="tooltip">{t('cashUp.cashupTableRight.tooltip.cashPayments')}</span>
                </div>
              </td>
              <td>
                <span style={{ width: '92px' }}>
                  <CurrencySign value={cashTakings} />
                </span>
              </td>
            </tr>
            <tr>
              <td>
                <span>{`- ${t('cashUp.cashupTableRight.tableRow.pettyCash')}`}</span>
                <div className="info">
                  <span className="tooltip">{t('cashUp.cashupTableRight.tooltip.pettyCash')}</span>
                </div>
              </td>
              <td>
                {byRangeModal === 'by-day' && (
                  <input
                    type="text"
                    placeholder={t('common.placeholder.enterAmount')}
                    value={pettyCashOut}
                    onKeyDown={keyPress}
                    onChange={(event) => onInputChange(event.target.value, 'pettyCashOut')}
                  />
                )}
                {byRangeModal === 'by-range' && (
                  <span>
                    {typeof pettyCashOut === 'number' ? (
                      <>
                        <CurrencySign /> {pettyCashOut}
                      </>
                    ) : (
                      '-'
                    )}
                  </span>
                )}
              </td>
            </tr>
            <tr>
              <td>
                <span>{`- ${t('cashUp.cashupTableRight.tableRow.cashTaken')}`}</span>
                <div className="info">
                  <span className="tooltip">{t('cashUp.cashupTableRight.tooltip.cashTaken')}</span>
                </div>
              </td>
              <td>
                {byRangeModal === 'by-day' && (
                  <input
                    type="text"
                    placeholder={t('common.placeholder.enterAmount')}
                    value={cashTakenToBankTotal}
                    onKeyDown={keyPress}
                    onChange={(event) => onInputChange(event.target.value, 'cashTakenToBank')}
                  />
                )}
                {byRangeModal === 'by-range' && (
                  <span>
                    {typeof cashTakenToBankTotal === 'number' ? (
                      <>
                        <CurrencySign /> {cashTakenToBankTotal}
                      </>
                    ) : (
                      '-'
                    )}
                  </span>
                )}
              </td>
            </tr>
            <tr>
              <td>
                <span className="text-bold">{t('cashUp.cashupTableRight.tableRow.expectedTillCashPart1')}</span>
                {t('cashUp.cashupTableRight.tableRow.expectedTillCashPart2')}
                <div className="info">
                  <span className="tooltip">{t('cashUp.cashupTableRight.tooltip.expectedTillCash')}</span>
                </div>
              </td>
              <td>
                <span>
                  <CurrencySign value={expectedCashInTill} />
                </span>
              </td>
            </tr>
            <tr>
              <td>
                <span>{t('cashUp.cashupTableRight.tableRow.actualCash')}</span>
                <div className="info">
                  <span className="tooltip">{t('cashUp.cashupTableRight.tooltip.actualCash')}</span>
                </div>
              </td>
              <td>
                {byRangeModal === 'by-day' && (
                  <input
                    type="text"
                    placeholder={t('common.placeholder.enterAmount')}
                    value={cashInTillEnd}
                    onKeyDown={keyPress}
                    onChange={(event) => onInputChange(event.target.value, 'actualCashInTill')}
                  />
                )}
                {byRangeModal === 'by-range' && (
                  <span>
                    {typeof cashInTillEnd === 'number' ? (
                      <>
                        <CurrencySign /> {cashInTillEnd}
                      </>
                    ) : (
                      '-'
                    )}
                  </span>
                )}
              </td>
            </tr>
            <tr>
              <td>
                <span>{t('cashUp.cashupTableRight.tableRow.differenceCash')}</span>
                <div className="info">
                  <span className="tooltip">{t('cashUp.cashupTableRight.tooltip.differenceCash')}</span>
                </div>
              </td>
              <td>
                <span>
                  <CurrencySign value={differenceActualVsExpected} />
                </span>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default CashupTableRight;
